import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { HazardLayer, HazardRiskLevel, HazardType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const EOC_ROLES = ['DUTY_OFFICER', 'EOC_COMMANDER'];
const HAZARDS: HazardType[] = ['CYCLONE', 'FLOOD', 'TSUNAMI', 'LANDSLIDE', 'MARINE', 'HEALTH'];
const RISK_LEVELS: HazardRiskLevel[] = ['HIGH', 'MODERATE', 'LOW'];
const DISTRICTS = [
  'Beau Vallon', 'Anse Royale', 'Baie Lazare', 'Anse Boileau', 'Takamaka', 'Port Glaud', 'Cascade', 'Au Cap',
  'Anse Aux Pins', 'English River', 'Mont Buxton', 'Plaisance', 'St Louis', 'Roche Caiman', 'Bel Air', 'Mont Fleuri',
  'Glacis', 'Bel Ombre', 'Anse Etoile', 'Machabee', 'Pointe Larue', 'Grand Anse Mahe', 'Grand Anse Praslin', 'Baie Ste Anne',
];

type Tab = 'layers' | 'check';

/** Local hazard-zone risk-layer registry, hand-rolled point-in-polygon check (no PostGIS in this environment) (M36). */
export function HazardLayers() {
  const { persona } = useAuth();
  const isEoc = persona ? persona.roles.some((r) => EOC_ROLES.includes(r)) : false;
  const [tab, setTab] = useState<Tab>('layers');

  return (
    <div>
      <PageHeader title="Hazard Layers" subtitle="Local hazard-zone risk polygons (CYCLONE track cones, FLOOD/TSUNAMI/LANDSLIDE zones) + a point-in-polygon check (M36)." />
      <TabStrip tabs={[{ key: 'layers', label: 'Layers' }, { key: 'check', label: 'Point-in-polygon check' }]} active={tab} onChange={setTab} />
      {!isEoc ? <RoleNotice roles={EOC_ROLES} /> : null}
      {tab === 'layers' ? <LayersTab isEoc={isEoc} /> : <CheckTab isEoc={isEoc} />}
    </div>
  );
}

function LayersTab({ isEoc }: { isEoc: boolean }) {
  const [hazard, setHazard] = useState('');
  const [district, setDistrict] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (hazard) params.set('hazard', hazard);
    if (district) params.set('district', district);
    return `/api/v1/m36/hazard-layers?${params.toString()}`;
  }, [hazard, district]);
  const listQ = useApiQuery<Page<HazardLayer>>(isEoc ? path : null);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex gap-2">
          <select className="field-input !w-auto" value={hazard} onChange={(e) => setHazard(e.target.value)}>
            <option value="">All hazards</option>
            {HAZARDS.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
          <select className="field-input !w-auto" value={district} onChange={(e) => setDistrict(e.target.value)}>
            <option value="">All districts</option>
            {DISTRICTS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        {isEoc ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Register hazard layer
          </button>
        ) : null}
      </div>

      {isEoc ? (
        <>
          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No hazard layers registered" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Name</th>
                    <th className="table-head">Hazard</th>
                    <th className="table-head">District</th>
                    <th className="table-head">Risk</th>
                    <th className="table-head">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((l) => (
                    <tr key={l.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(l.id)}>
                      <td className="table-cell font-medium">{l.name}</td>
                      <td className="table-cell">
                        <StatusBadge status={l.hazard} />
                      </td>
                      <td className="table-cell text-xs text-slate-500">{l.district ?? 'multi-district'}</td>
                      <td className="table-cell">
                        <StatusBadge status={l.riskLevel} />
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={l.active ? 'ACTIVE' : 'CLOSED'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {selectedId ? (
        <DetailModal id={selectedId} isEoc={isEoc} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DetailModal({ id, isEoc, onClose, onChanged }: { id: string; isEoc: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<HazardLayer>(`/api/v1/m36/hazard-layers/${id}`);
  const l = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [riskLevel, setRiskLevel] = useState<HazardRiskLevel | ''>('');

  const save = async (patch: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m36/hazard-layers/${id}`, patch);
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={l ? l.name : 'Hazard layer'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {l ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <Detail label="Hazard" value={<StatusBadge status={l.hazard} />} />
            <Detail label="Risk level" value={<StatusBadge status={l.riskLevel} />} />
            <Detail label="District" value={l.district ?? '—'} />
            <Detail label="Active" value={<StatusBadge status={l.active ? 'ACTIVE' : 'CLOSED'} />} />
            <Detail label="Source" value={l.source} />
            <Detail label="Effective" value={`${formatDate(l.effectiveFrom)} → ${l.effectiveTo ? formatDate(l.effectiveTo) : 'open'}`} />
          </div>
          {isEoc ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="flex gap-2">
                <select className="field-input !w-auto" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value as HazardRiskLevel)}>
                  <option value="">Update risk level…</option>
                  {RISK_LEVELS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <button className="btn-secondary" disabled={busy || !riskLevel} onClick={() => save({ riskLevel })}>
                  Save
                </button>
              </div>
              <button className={l.active ? 'btn-danger' : 'btn-success'} disabled={busy} onClick={() => save({ active: !l.active })}>
                {l.active ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [hazard, setHazard] = useState<HazardType>('FLOOD');
  const [name, setName] = useState('');
  const [district, setDistrict] = useState('');
  const [riskLevel, setRiskLevel] = useState<HazardRiskLevel>('MODERATE');
  const [source, setSource] = useState('');
  const [coordinatesText, setCoordinatesText] = useState('[[55.44,-4.63],[55.47,-4.63],[55.47,-4.60],[55.44,-4.60],[55.44,-4.63]]');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      let ring: unknown;
      try {
        ring = JSON.parse(coordinatesText);
      } catch {
        throw new Error('Coordinates must be valid JSON — an array of [lng, lat] pairs forming a closed ring');
      }
      await api.post('/api/v1/m36/hazard-layers', {
        hazard,
        name,
        district: district || undefined,
        riskLevel,
        geometry: { type: 'Polygon', coordinates: [ring] },
        source,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : e instanceof Error ? e.message : 'Failed to register layer');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register hazard-zone polygon" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hazard type">
            <select className="field-input" value={hazard} onChange={(e) => setHazard(e.target.value as HazardType)}>
              {HAZARDS.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Risk level">
            <select className="field-input" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value as HazardRiskLevel)}>
              {RISK_LEVELS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name">
            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Roche Caiman flood zone" />
          </Field>
          <Field label="District (optional — leave blank if multi-district)">
            <select className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)}>
              <option value="">—</option>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Source">
          <input className="field-input" value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. NDRMD flood risk map 2024" />
        </Field>
        <Field label="Polygon ring coordinates (GeoJSON [lng,lat] pairs, closed ring)">
          <textarea className="field-input font-mono text-xs" rows={3} value={coordinatesText} onChange={(e) => setCoordinatesText(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !name || !source} onClick={submit}>
            {busy ? 'Registering…' : 'Register layer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CheckTab({ isEoc }: { isEoc: boolean }) {
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [hazard, setHazard] = useState('');
  const [result, setResult] = useState<{ point: { lat: number; lng: number }; hits: Array<{ id: string; hazard: string; name: string; riskLevel: string }> } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const params = new URLSearchParams({ lat, lng });
      if (hazard) params.set('hazard', hazard);
      const res = await api.get<typeof result>(`/api/v1/m36/hazard-layers/check?${params.toString()}`);
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Check failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card max-w-xl p-5">
      {!isEoc ? <RoleNotice roles={EOC_ROLES} /> : null}
      {error ? <ErrorNote message={error} /> : null}
      <div className="grid grid-cols-3 gap-2">
        <input className="field-input" type="number" step="0.000001" placeholder="Latitude" value={lat} onChange={(e) => setLat(e.target.value)} />
        <input className="field-input" type="number" step="0.000001" placeholder="Longitude" value={lng} onChange={(e) => setLng(e.target.value)} />
        <select className="field-input" value={hazard} onChange={(e) => setHazard(e.target.value)}>
          <option value="">Any hazard</option>
          {HAZARDS.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
      </div>
      <button className="btn-primary mt-3" disabled={busy || !lat || !lng} onClick={check}>
        Check point
      </button>
      {result ? (
        <div className="mt-3">
          {result.hits.length === 0 ? (
            <p className="text-sm text-seagreen-600">No active hazard layer contains this point.</p>
          ) : (
            <div className="space-y-1.5">
              {result.hits.map((h) => (
                <div key={h.id} className="flex items-center justify-between rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm">
                  <span>
                    {h.name} (<StatusBadge status={h.hazard} />)
                  </span>
                  <StatusBadge status={h.riskLevel} />
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
